import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n/config';
import App from './App';
import { logger } from './utils/logger';
import { isRecorderBusy, whenRecorderIdle } from './utils/recorderEvents';

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>
);

// Auto-recover wanneer een gehashte JS/CSS bundle na een deploy niet meer bestaat
// en de server de SPA-fallback (HTML) terugstuurt. De browser krijgt dan een
// SyntaxError ("Unexpected token '<'") of een ChunkLoadError, wat een witscherm
// veroorzaakt. We legen de caches, unregistreren de service worker en herladen
// één keer; een sessionStorage-flag voorkomt een reload-loop.
const RECOVERY_FLAG = 'cache-recovery-in-progress';

// Voorkomt dat meerdere uitgestelde recoveries op dezelfde opname staan te wachten
let recoveryDeferred = false;

async function recoverFromStaleCache(reason: string): Promise<void> {
    if (sessionStorage.getItem(RECOVERY_FLAG)) {
        logger.error('Cache recovery already attempted, niet opnieuw reloaden:', reason);
        return;
    }

    // Een lopende opname mag nooit door een reload onderbroken worden. De pagina
    // is dan wel stuk (missende bundle), maar de recorder draait door; we
    // herstellen zodra de opname is afgerond en geüpload.
    if (isRecorderBusy()) {
        if (recoveryDeferred) {
            return;
        }
        recoveryDeferred = true;
        logger.warn('Stale cache gedetecteerd tijdens opname, herstel uitgesteld:', reason);
        await whenRecorderIdle();
        recoveryDeferred = false;

        if (sessionStorage.getItem(RECOVERY_FLAG)) {
            logger.error('Cache recovery already attempted, niet opnieuw reloaden:', reason);
            return;
        }
    }

    sessionStorage.setItem(RECOVERY_FLAG, '1');
    logger.warn('Stale cache gedetecteerd, recover en reload:', reason);

    try {
        if ('caches' in window) {
            const names = await caches.keys();
            await Promise.all(names.map((n) => caches.delete(n)));
        }
        if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
        }
    } catch (err) {
        logger.error('Cache recovery faalde:', err);
    }

    window.location.reload();
}

// Clear de loop-guard zodra de app daadwerkelijk werkt
window.addEventListener('load', () => {
    setTimeout(() => sessionStorage.removeItem(RECOVERY_FLAG), 5000);
});

// 1. Resource load errors (script/link tags) — bubblen niet, dus capture-phase
window.addEventListener(
    'error',
    (event) => {
        const target = event.target as HTMLElement | null;
        if (!target || target === (window as unknown as HTMLElement)) {
            // Runtime SyntaxError op een hashed bundle
            const msg = event.message || '';
            const filename = event.filename || '';
            if (
                /Unexpected token '<'/.test(msg) &&
                /\/static\/(js|css)\//.test(filename)
            ) {
                void recoverFromStaleCache(`SyntaxError in ${filename}`);
            }
            return;
        }
        const tag = target.tagName;
        if (tag === 'SCRIPT' || tag === 'LINK') {
            const src =
                (target as HTMLScriptElement).src ||
                (target as HTMLLinkElement).href ||
                '';
            if (/\/static\/(js|css)\//.test(src)) {
                void recoverFromStaleCache(`Failed to load ${src}`);
            }
        }
    },
    true,
);

// 2. Dynamic import / code-split chunk load failures
window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
        (reason && (reason.message || reason.toString())) || '';
    if (
        /Loading chunk [\w-]+ failed/i.test(message) ||
        /Loading CSS chunk [\w-]+ failed/i.test(message) ||
        /ChunkLoadError/i.test(message)
    ) {
        void recoverFromStaleCache(`Chunk load failure: ${message}`);
    }
});

// Registreer Service Worker alleen in productie
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
            .then((registration) => {
                logger.log('SW registered: ', registration);

                // Check voor updates
                registration.addEventListener('updatefound', () => {
                    const newWorker = registration.installing;
                    if (newWorker) {
                        newWorker.addEventListener('statechange', () => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                // Nieuwe versie beschikbaar
                                logger.log('New version available');
                                window.dispatchEvent(new CustomEvent('sw-update-available'));
                            }
                        });
                    }
                });
            })
            .catch((registrationError) => {
                logger.log('SW registration failed: ', registrationError);
            });
    });
} else if ('serviceWorker' in navigator && process.env.NODE_ENV === 'development') {
    // In development: unregister eventuele bestaande service workers
    navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
            registration.unregister();
            logger.log('Unregistered SW for development:', registration);
        }
    });
} 